import React from 'react';

const PrivacyPolicy = () => {
  const styles = {
    container: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: 1.6,
      color: '#333',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem',
      background: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderRadius: '8px',
      marginTop: '2rem',
      marginBottom: '2rem'
    },
    backLink: {
      display: 'inline-block',
      marginBottom: '2rem',
      color: '#1e40af',
      textDecoration: 'none',
      fontWeight: 500
    },
    title: {
      color: '#1e40af',
      fontSize: '2.5rem',
      marginBottom: '1rem',
      textAlign: 'center'
    },
    lastUpdated: {
      textAlign: 'center',
      fontStyle: 'italic',
      color: '#6b7280',
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '4px'
    },
    heading: {
      color: '#1e40af',
      fontSize: '1.5rem',
      marginTop: '2rem',
      marginBottom: '1rem',
      borderBottom: '2px solid #e5e7eb',
      paddingBottom: '0.5rem'
    },
    subheading: {
      color: '#374151',
      fontSize: '1.25rem',
      marginTop: '1.5rem',
      marginBottom: '0.75rem'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#4b5563'
    },
    list: {
      marginLeft: '2rem',
      marginBottom: '1rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#4b5563'
    },
    contactInfo: {
      backgroundColor: '#eff6ff',
      padding: '1.5rem',
      borderRadius: '8px',
      marginTop: '2rem',
      borderLeft: '4px solid #1e40af'
    },
    highlight: {
      backgroundColor: '#fef3c7',
      padding: '1rem',
      borderRadius: '4px',
      margin: '1rem 0',
      borderLeft: '4px solid #f59e0b'
    },
    highlightText: {
      marginBottom: '1rem',
      color: '#4b5563',
      fontStyle: 'italic'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <a href="/" style={styles.backLink}>← Back to PersonalityAI</a>
        
        <h1 style={styles.title}>PersonalityAI Privacy Policy</h1>
        
        <div style={styles.lastUpdated}>
          <strong>Last Updated:</strong> July 07, 2025<br />
          <strong>Effective Date:</strong> July 07, 2025
        </div>

        <h2 style={styles.heading}>1. Introduction</h2>
        <p style={styles.paragraph}>
          PersonalityAI ("we," "our," or "us") operates the PersonalityAI browser extension and web application (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
        </p>
        <p style={styles.paragraph}>
          By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, do not use our Service.
        </p>

        <h2 style={styles.heading}>2. Information We Collect</h2>

        <h3 style={styles.subheading}>2.1 Personal Information</h3>
        <p style={styles.paragraph}>
          We may collect personally identifiable information that you voluntarily provide to us when:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Creating an account (email address, name)</li>
          <li style={styles.listItem}>Subscribing to our services</li>
          <li style={styles.listItem}>Contacting us for support</li>
        </ul>

        <h3 style={styles.subheading}>2.2 Usage and Behavioral Data</h3>
        <p style={styles.paragraph}>
          When you use our browser extension, we automatically collect:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Interaction Data</strong>: Frequency of AI prompt enhancements, platforms used</li>
          <li style={styles.listItem}><strong>Behavioral Analytics</strong>: Keystroke patterns, typing speed, correction frequency, session duration</li>
          <li style={styles.listItem}><strong>Performance Metrics</strong>: Response times, error rates, feature usage</li>
          <li style={styles.listItem}><strong>Technical Data</strong>: Browser type, extension version, operating system</li>
          <li style={styles.listItem}><strong>Session Information</strong>: Date/time of usage, duration of sessions</li>
        </ul>

        <h3 style={styles.subheading}>2.3 AI Platform Data</h3>
        <p style={styles.paragraph}>
          Our extension operates on various AI platforms (ChatGPT, Claude, Gemini, etc.). We collect:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Platform usage statistics</li>
          <li style={styles.listItem}>Prompt enhancement frequency</li>
          <li style={styles.listItem}>Quality improvement metrics</li>
          <li style={styles.listItem}>User satisfaction indicators</li>
        </ul>

        <h3 style={styles.subheading}>2.4 Payment Information</h3>
        <p style={styles.paragraph}>
          Payment processing is handled by Stripe. We do not store your full payment card details but may retain:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Payment method type (last 4 digits of card)</li>
          <li style={styles.listItem}>Billing address</li>
          <li style={styles.listItem}>Payment history</li>
          <li style={styles.listItem}>Subscription status</li>
        </ul>

        <h2 style={styles.heading}>3. How We Use Your Information</h2>

        <h3 style={styles.subheading}>3.1 Primary Uses</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Service Provision</strong>: Delivering personalized AI prompt enhancements</li>
          <li style={styles.listItem}><strong>Account Management</strong>: Managing your account, subscription, and preferences</li>
          <li style={styles.listItem}><strong>Quality Improvement</strong>: Analyzing usage patterns to improve our algorithms</li>
          <li style={styles.listItem}><strong>Communication</strong>: Sending service-related notifications and updates</li>
        </ul>

        <h3 style={styles.subheading}>3.2 Behavioral Analytics</h3>
        <p style={styles.paragraph}>
          We use behavioral data to:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Personalize your AI interactions</li>
          <li style={styles.listItem}>Improve our machine learning models</li>
          <li style={styles.listItem}>Generate usage insights and recommendations</li>
          <li style={styles.listItem}>Optimize extension performance</li>
        </ul>

        <h3 style={styles.subheading}>3.3 Revenue Sharing Program</h3>
        <p style={styles.paragraph}>
          For users participating in our revenue sharing program:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Quality scoring based on interaction patterns</li>
          <li style={styles.listItem}>Payment calculations based on algorithmic assessment</li>
          <li style={styles.listItem}>Performance tracking for payout eligibility</li>
        </ul>

        <h2 style={styles.heading}>4. Data Sharing and Disclosure</h2>

        <h3 style={styles.subheading}>4.1 Third-Party Services</h3>
        <p style={styles.paragraph}>
          We share data with:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Stripe</strong>: Payment processing and subscription management</li>
          <li style={styles.listItem}><strong>Supabase</strong>: Database hosting and user authentication</li>
          <li style={styles.listItem}><strong>AI Platform APIs</strong>: When explicitly authorized by you</li>
        </ul>

        <h3 style={styles.subheading}>4.2 Legal Requirements</h3>
        <p style={styles.paragraph}>
          We may disclose your information if required by law or in response to:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Court orders or legal process</li>
          <li style={styles.listItem}>Government requests</li>
          <li style={styles.listItem}>Protection of our rights and safety</li>
          <li style={styles.listItem}>Investigation of fraud or security issues</li>
        </ul>

        <h3 style={styles.subheading}>4.3 Business Transfers</h3>
        <p style={styles.paragraph}>
          In the event of a merger, acquisition, or sale, your information may be transferred to the successor entity.
        </p>

        <h2 style={styles.heading}>5. Data Security</h2>

        <h3 style={styles.subheading}>5.1 Security Measures</h3>
        <p style={styles.paragraph}>
          We implement industry-standard security measures including:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Encryption of data in transit and at rest</li>
          <li style={styles.listItem}>Regular security audits and updates</li>
          <li style={styles.listItem}>Access controls and authentication</li>
          <li style={styles.listItem}>Secure hosting infrastructure</li>
        </ul>

        <h3 style={styles.subheading}>5.2 Data Retention</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Account data: Retained while your account is active</li>
          <li style={styles.listItem}>Behavioral data: Retained for up to 2 years for analytics</li>
          <li style={styles.listItem}>Payment data: Retained per legal requirements (typically 7 years)</li>
          <li style={styles.listItem}>Deleted account data: Permanently deleted within 30 days</li>
        </ul>

        <h2 style={styles.heading}>6. Your Rights and Choices</h2>

        <h3 style={styles.subheading}>6.1 Access and Control</h3>
        <p style={styles.paragraph}>
          You have the right to:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Access your personal information</li>
          <li style={styles.listItem}>Correct inaccurate information</li>
          <li style={styles.listItem}>Delete your account and associated data</li>
          <li style={styles.listItem}>Export your data in a portable format</li>
          <li style={styles.listItem}>Opt-out of behavioral analytics</li>
        </ul>

        <h3 style={styles.subheading}>6.2 Communication Preferences</h3>
        <p style={styles.paragraph}>
          You can:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Unsubscribe from marketing communications</li>
          <li style={styles.listItem}>Manage notification preferences</li>
          <li style={styles.listItem}>Control data collection settings</li>
        </ul>

        <h3 style={styles.subheading}>6.3 Data Portability</h3>
        <p style={styles.paragraph}>
          Upon request, we will provide your data in a structured, commonly used format.
        </p>

        <h2 style={styles.heading}>7. Cookies and Tracking</h2>

        <h3 style={styles.subheading}>7.1 Cookies We Use</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Essential Cookies</strong>: Required for basic functionality</li>
          <li style={styles.listItem}><strong>Analytics Cookies</strong>: Used to understand usage patterns</li>
          <li style={styles.listItem}><strong>Preference Cookies</strong>: Store your settings and preferences</li>
        </ul>

        <h3 style={styles.subheading}>7.2 Third-Party Cookies</h3>
        <p style={styles.paragraph}>
          Our service may contain links to third-party websites with their own privacy policies.
        </p>

        <h2 style={styles.heading}>8. Children's Privacy</h2>
        <p style={styles.paragraph}>
          Our Service is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided personal information, please contact us immediately.
        </p>

        <h2 style={styles.heading}>9. International Data Transfers</h2>
        <p style={styles.paragraph}>
          Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for international transfers.
        </p>

        <h2 style={styles.heading}>10. California Privacy Rights (CCPA)</h2>
        <p style={styles.paragraph}>
          California residents have additional rights under the California Consumer Privacy Act:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Right to know what personal information is collected</li>
          <li style={styles.listItem}>Right to delete personal information</li>
          <li style={styles.listItem}>Right to opt-out of sale of personal information</li>
          <li style={styles.listItem}>Right to non-discrimination</li>
        </ul>

        <h2 style={styles.heading}>11. European Privacy Rights (GDPR)</h2>
        <p style={styles.paragraph}>
          If you are in the European Economic Area, you have rights under the General Data Protection Regulation:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Legal Basis</strong>: We process data based on consent, contract performance, or legitimate interests</li>
          <li style={styles.listItem}><strong>Data Protection Officer</strong>: Contact support@personalityai.com</li>
          <li style={styles.listItem}><strong>Supervisory Authority</strong>: You may file complaints with your local data protection authority</li>
        </ul>

        <h2 style={styles.heading}>12. Changes to This Privacy Policy</h2>
        <p style={styles.paragraph}>
          We may update this Privacy Policy periodically. We will notify you of material changes by:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Email notification to registered users</li>
          <li style={styles.listItem}>Prominent notice on our website</li>
          <li style={styles.listItem}>In-app notification in the extension</li>
        </ul>

        <h2 style={styles.heading}>13. Contact Information</h2>
        <div style={styles.contactInfo}>
          <p style={styles.paragraph}>
            <strong>PersonalityAI Support Team</strong><br />
            Email: support@personalityai.com
          </p>
          
          <p style={styles.paragraph}>
            <strong>Data Protection Officer</strong><br />
            Email: support@personalityai.com
          </p>
        </div>

        <h2 style={styles.heading}>14. Consent</h2>
        <p style={styles.paragraph}>
          By using our Service, you consent to our Privacy Policy and agree to its terms. If you do not agree, please discontinue use of our Service.
        </p>

        <div style={styles.highlight}>
          <p style={styles.highlightText}>
            This Privacy Policy is governed by applicable laws and is subject to change without notice. Please review regularly for updates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
